import { Checkbox } from "design-system/atoms/Checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "design-system/molecules/Command"
import { formatNumber } from "lopermut"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"

type Props = {
  selectedValues: string[]
  onChange: (segmentIds: string[]) => void
  children: ReactNode
}

export function SegmentsPicker({ selectedValues, onChange, children }: Props) {
  const { t } = useTranslation()
  const { data: segmentGroups = [] } = useSegmentGroupsQuery()
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()

  function toggle(segmentId: string) {
    onChange(
      selectedValues.includes(segmentId)
        ? selectedValues.filter((id) => id !== segmentId)
        : [...selectedValues, segmentId],
    )
  }

  function toggleGroup(segmentIds: string[]) {
    const isWholeGroupSelected = segmentIds.every((id) => selectedValues.includes(id))

    onChange(
      isWholeGroupSelected
        ? selectedValues.filter((id) => !segmentIds.includes(id))
        : [...new Set([...selectedValues, ...segmentIds])],
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="p-0 w-(--radix-popover-trigger-width) min-w-72" align="start">
        <Command>
          <CommandInput placeholder={t("campaigns.launchAudience.searchSegments")} />
          <CommandList className="max-h-80">
            <CommandEmpty className="p-4 text-center text-sm text-muted-foreground">
              {t("campaigns.launchAudience.noSegments")}
            </CommandEmpty>
            {segmentGroups.map((group) => {
              const groupName = getSegmentGroupTranslation(group) || group.name
              const segmentIds = group.segments.map((segment) => segment.id)
              const isWholeGroupSelected = segmentIds.every((id) => selectedValues.includes(id))

              return (
                <CommandGroup
                  key={group.id}
                  heading={
                    <div className="flex items-center justify-between gap-2">
                      <span>{groupName}</span>
                      <button
                        type="button"
                        onClick={() => toggleGroup(segmentIds)}
                        className="hover:text-foreground"
                      >
                        {isWholeGroupSelected
                          ? t("campaigns.launchAudience.clearGroup")
                          : t("campaigns.launchAudience.selectGroup")}
                      </button>
                    </div>
                  }
                >
                  {group.segments.map((segment) => {
                    const segmentName = getSegmentTranslation(segment.name, group)

                    return (
                      <CommandItem
                        key={segment.id}
                        value={`${groupName} ${segmentName} ${segment.id}`}
                        onSelect={() => toggle(segment.id)}
                        className="gap-2"
                      >
                        <Checkbox checked={selectedValues.includes(segment.id)} />
                        <span className="flex-1 truncate">{segmentName}</span>
                        <span className="font-mono text-xs text-muted-foreground tabular-nums">
                          {formatNumber({ amount: segment.count, fix: 0 })}
                        </span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
